import api from './api';

/**
 * VPP Monitoring Service
 *
 * API client for VPP statistics and monitoring
 * Phase 4.4: Monitoring & Analytics
 *
 * NOTE: Flow statistics (IPFIX data) are now queried via PromQL from VictoriaMetrics.
 * Use observabilityService.queryRange() with queries like:
 *   - edge_ipfix_bytes_total{edge_id="..."}
 *   - edge_ipfix_packets_total{edge_id="..."}
 *   - edge_ipfix_flows_total{edge_id="..."}
 * See TrafficMonitoring.tsx for usage examples.
 */

export interface EdgeStatisticsSummary {
  edge_id: string;
  total_policies: number;
  total_rules: number;
  total_packets_forwarded: number;
  total_bytes_forwarded: number;
  total_packets_dropped: number;
  total_bytes_dropped: number;
  active_applications: number;
  total_bandwidth_mbps: number;
  last_collection_time?: string;
}

export interface ABFTimeSeriesData {
  edge_id: string;
  policy_id: string;
  hours_back: number;
  data_points: number;
  data: ABFStatisticsPoint[];
}

export interface ABFStatisticsPoint {
  id: string;
  edge_id: string;
  policy_id: string;
  packets_matched: number;
  bytes_matched: number;
  packets_forwarded: number;
  bytes_forwarded: number;
  packets_dropped: number;
  bytes_dropped: number;
  sample_time: string;
}

export interface AppTimeSeriesData {
  edge_id: string;
  app_id: string;
  hours_back: number;
  data_points: number;
  data: AppTrafficPoint[];
}

export interface AppTrafficPoint {
  id: string;
  edge_id: string;
  app_definition_id: string;
  packets_tx: number;
  bytes_tx: number;
  packets_rx: number;
  bytes_rx: number;
  avg_latency_ms: string;
  avg_jitter_ms: string;
  packet_loss_rate: string;
  bandwidth_mbps: string;
  sample_time: string;
  sample_duration_sec: number;
}

export interface TopApplication {
  app_name: string;
  bandwidth_mbps: number;
  app_id: string;
}

export interface TopApplicationsResponse {
  edge_id: string;
  top_applications: TopApplication[];
}

export interface CollectionResult {
  edge_id: string;
  abf_records_collected: number;
  app_records_collected: number;
  collection_time: string;
}

export interface CollectionAllResult {
  edges_processed: number;
  abf_records: number;
  app_records: number;
}

export interface FlowprobeConfig {
  enabled: boolean;
  collector_address: string;
  collector_port: number;
  vrf_id?: number;
  path_mtu?: number;
  template_interval?: number;
  active_timer?: number;
  passive_timer?: number;
  record_l2?: boolean;
  record_l3?: boolean;
  record_l4?: boolean;
  interfaces?: string[];
}

export interface FlowprobeConfigResponse {
  edge_id: string;
  config: FlowprobeConfig;
}

const vppMonitoringService = {
  /**
   * Manually trigger statistics collection for an edge
   */
  collectStatistics: async (edgeId: string) => {
    const response = await api.post(`/vpp/monitoring/collect/${edgeId}`);
    return response.data as CollectionResult;
  },

  /**
   * Trigger statistics collection for all edges
   */
  collectAllStatistics: async () => {
    const response = await api.post('/vpp/monitoring/collect-all');
    return response.data as CollectionAllResult;
  },

  /**
   * Get statistics summary for an edge
   */
  getSummary: async (edgeId: string) => {
    const response = await api.get(`/vpp/monitoring/summary/${edgeId}`);
    return response.data as EdgeStatisticsSummary;
  },

  /**
   * Get time-series ABF statistics for a policy
   */
  getABFTimeSeries: async (edgeId: string, policyId: string, hoursBack: number = 24) => {
    const response = await api.get(`/vpp/monitoring/abf/${edgeId}/${policyId}/timeseries`, {
      params: { hoursBack },
    });
    return response.data as ABFTimeSeriesData;
  },

  /**
   * Get time-series application traffic statistics
   */
  getAppTimeSeries: async (edgeId: string, appId: string, hoursBack: number = 24) => {
    const response = await api.get(`/vpp/monitoring/app/${edgeId}/${appId}/timeseries`, {
      params: { hoursBack },
    });
    return response.data as AppTimeSeriesData;
  },

  /**
   * Get top applications by bandwidth
   */
  getTopApplications: async (edgeId: string, limit: number = 10) => {
    const response = await api.get(`/vpp/monitoring/top-apps/${edgeId}`, {
      params: { limit },
    });
    return response.data as TopApplicationsResponse;
  },

  /**
   * Get recent ABF statistics
   */
  getRecentABFStats: async (edgeId: string, policyId?: string) => {
    const response = await api.get(`/vpp/monitoring/abf/${edgeId}`, {
      params: policyId ? { policyId } : undefined,
    });
    return response.data as ABFStatisticsPoint[];
  },

  /**
   * Get recent application traffic statistics
   */
  getRecentAppStats: async (edgeId: string, appId?: string) => {
    const response = await api.get(`/vpp/monitoring/apps/${edgeId}`, {
      params: appId ? { appId } : undefined,
    });
    return response.data as AppTrafficPoint[];
  },

  /**
   * Cleanup old statistics
   */
  cleanupOldStatistics: async () => {
    const response = await api.post('/vpp/monitoring/cleanup');
    return response.data as { abf_deleted: number; app_deleted: number };
  },

  // ==========================================
  // FLOWPROBE CONFIGURATION
  // ==========================================

  /**
   * Get flowprobe configuration for an edge
   */
  getFlowprobeConfig: async (edgeId: string) => {
    const response = await api.get(`/vpp/monitoring/flowprobe/${edgeId}`);
    return response.data as FlowprobeConfigResponse;
  },

  /**
   * Update flowprobe configuration
   */
  updateFlowprobeConfig: async (edgeId: string, config: Partial<FlowprobeConfig>) => {
    const response = await api.put(`/vpp/monitoring/flowprobe/${edgeId}`, config);
    return response.data as FlowprobeConfigResponse & { message: string };
  },
};

export default vppMonitoringService;
